// x402 payment protocol helpers.
// Implements the client-side of x402 v2: parse 402 response, build EIP-712 typed data,
// encode the PAYMENT-SIGNATURE header.

import { type Address, type Hex, encodePacked, keccak256 } from "viem";

// --- Types from grpc-gateway-x402 ---

export interface PaymentAccept {
  scheme: "exact";
  network: string; // CAIP-2, e.g. "eip155:8453"
  amount: string; // atomic units e.g. "500000"
  asset: string; // token contract address
  payTo: string; // recipient wallet
  maxTimeoutSeconds: number;
  extra: {
    name: string; // the token contract's EIP-712 domain name, e.g. "USD Coin"
    version: string; // EIP-712 domain version: "2"
  };
}

export interface PaymentRequiredResponse {
  x402Version: number;
  error: string;
  accepts: PaymentAccept[];
}

export interface PaymentPayload {
  x402Version: number;
  accepted: {
    scheme: "exact";
    network: string;
    amount: string;
    asset: string;
    payTo: string;
  };
  payload: {
    signature: string;
    authorization: {
      from: string;
      to: string;
      value: string;
      validAfter: number;
      validBefore: number;
      nonce: string;
    };
  };
}

// --- Parse the PAYMENT-REQUIRED header ---

export function parsePaymentRequired(
  headerValue: string,
): PaymentRequiredResponse {
  const json = atob(headerValue);
  return JSON.parse(json) as PaymentRequiredResponse;
}

// --- Extract chain ID from CAIP-2 network string ---

export function chainIdFromNetwork(network: string): number {
  // "eip155:42161" → 42161
  const parts = network.split(":");
  return parseInt(parts[1], 10);
}

// --- Build EIP-712 typed data for TransferWithAuthorization ---

export function buildEIP712TypedData(
  accept: PaymentAccept,
  from: Address,
) {
  const chainId = chainIdFromNetwork(accept.network);
  const now = Math.floor(Date.now() / 1000);
  const validAfter = now - 60; // 1 minute ago (clock skew tolerance)
  const validBefore = now + accept.maxTimeoutSeconds;
  const nonce = keccak256(
    encodePacked(
      ["address", "uint256"],
      [from, BigInt(now)],
    ),
  );

  const domain = {
    name: accept.extra.name,
    version: accept.extra.version,
    chainId: BigInt(chainId),
    verifyingContract: accept.asset as Address,
  } as const;

  const types = {
    TransferWithAuthorization: [
      { name: "from", type: "address" },
      { name: "to", type: "address" },
      { name: "value", type: "uint256" },
      { name: "validAfter", type: "uint256" },
      { name: "validBefore", type: "uint256" },
      { name: "nonce", type: "bytes32" },
    ],
  } as const;

  const message = {
    from,
    to: accept.payTo as Address,
    value: BigInt(accept.amount),
    validAfter: BigInt(validAfter),
    validBefore: BigInt(validBefore),
    nonce: nonce as Hex,
  };

  return { domain, types, primaryType: "TransferWithAuthorization" as const, message };
}

// --- Encode the PAYMENT-SIGNATURE header value ---

export function encodePaymentSignature(
  accept: PaymentAccept,
  signature: Hex,
  from: Address,
  typedData: ReturnType<typeof buildEIP712TypedData>,
): string {
  const payload: PaymentPayload = {
    x402Version: 2,
    accepted: {
      scheme: "exact",
      network: accept.network,
      amount: accept.amount,
      asset: accept.asset,
      payTo: accept.payTo,
    },
    payload: {
      signature,
      authorization: {
        from,
        to: accept.payTo,
        value: accept.amount,
        validAfter: Number(typedData.message.validAfter),
        validBefore: Number(typedData.message.validBefore),
        nonce: typedData.message.nonce,
      },
    },
  };
  return btoa(JSON.stringify(payload));
}

// --- Format atomic units to human-readable ---

export function formatAtomicUnits(
  amount: string,
  decimals: number,
): string {
  const num = BigInt(amount);
  const divisor = BigInt(10 ** decimals);
  const whole = num / divisor;
  const frac = num % divisor;
  const fracStr = frac.toString().padStart(decimals, "0").slice(0, 2);
  return `${whole}.${fracStr}`;
}
